import React, { useState, useMemo } from "react";
import { CATEGORIES, SEALED_ITEMS } from "../constants/shopData";
import { ArrowUpRight, MessageSquare, CalendarCheck2, Search, X } from "lucide-react";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function prefillContact(itemName) {
  window.dispatchEvent(
    new CustomEvent("rcs:inquire", {
      detail: {
        message: `I'd like to inquire about: ${itemName}. Please let me know availability and price, plus best times to come by.`,
      },
    })
  );
  setTimeout(() => scrollToId("contact"), 50);
}

function StockBadge({ qty }) {
  if (qty === undefined || qty === null) return null;
  const out = qty === 0;
  const low = qty > 0 && qty <= 2;
  const cls = out
    ? "bg-black text-white"
    : low
      ? "bg-[#FF1F1F] text-white"
      : "bg-white text-black";
  const label = out ? "Sold Out" : low ? `Only ${qty} Left` : `${qty} In Stock`;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1 border-2 border-black ${cls}`}
      data-testid="product-stock-badge"
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: out ? "#fff" : low ? "#fff" : "#FF1F1F" }}
      />
      {label}
    </span>
  );
}

function SealedInventory() {
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("All");

  const brands = useMemo(() => {
    const set = new Set(SEALED_ITEMS.map((i) => i.brand));
    return ["All", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SEALED_ITEMS.filter((it) => {
      if (brand !== "All" && it.brand !== brand) return false;
      if (!q) return true;
      return (
        it.name.toLowerCase().includes(q) ||
        it.set.toLowerCase().includes(q) ||
        it.brand.toLowerCase().includes(q)
      );
    });
  }, [query, brand]);

  const totalUnits = filtered.reduce((sum, i) => sum + (i.qty || 0), 0);

  return (
    <div data-testid="sealed-inventory">
      {/* Filter / search bar */}
      <div className="px-5 md:px-7 py-5 bg-white border-b-4 border-black flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="brand-filters">
          {brands.map((b) => {
            const isActive = brand === b;
            return (
              <button
                key={b}
                type="button"
                data-testid={`brand-filter-${b.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setBrand(b)}
                className={`font-heading uppercase text-sm tracking-wider px-3 py-2 border-4 border-black transition-all ${
                  isActive
                    ? "bg-[#FF1F1F] text-white shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]"
                    : "bg-white text-black hover:-translate-y-0.5"
                }`}
              >
                {b}
              </button>
            );
          })}
        </div>
        <div className="relative w-full lg:w-80">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-black/70 pointer-events-none"
          />
          <input
            data-testid="sealed-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search set, name, or brand…"
            className="nb-input pl-10 pr-10 !py-2.5"
          />
          {query && (
            <button
              type="button"
              data-testid="sealed-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black/70 hover:text-[#FF1F1F]"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="px-5 md:px-7 py-3 bg-[#F4F4F5] border-b-4 border-black flex items-center justify-between">
        <span
          className="font-mono text-xs uppercase tracking-widest font-bold"
          data-testid="inventory-result-count"
        >
          Showing {filtered.length} product{filtered.length === 1 ? "" : "s"} · {totalUnits} unit
          {totalUnits === 1 ? "" : "s"} in stock
        </span>
        <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-black/60">
          Prices confirmed in-shop · Ask about any item
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="px-6 py-16 text-center" data-testid="inventory-empty">
          <p className="font-heading uppercase text-3xl">No matches.</p>
          <p className="mt-2 text-sm text-black/70 font-mono uppercase tracking-widest">
            Try a different search or brand.
          </p>
        </div>
      ) : (
        <ul className="divide-y-4 divide-black max-h-[800px] overflow-y-auto" data-testid="inventory-list">
          {filtered.map((item, idx) => (
            <li
              key={`${item.brand}-${item.name}-${idx}`}
              data-testid={`product-item-sealed-${idx}`}
              className="px-5 md:px-7 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div className="flex items-start sm:items-center gap-4 flex-1 min-w-0">
                <span className="font-heading text-2xl text-[#FF1F1F] leading-none w-12 shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading uppercase text-lg sm:text-xl leading-tight break-words">
                    {item.name}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-black/60 mt-1">
                    {item.brand} · {item.set}
                  </div>
                  <div className="mt-2">
                    <StockBadge qty={item.qty} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:flex-col sm:items-end shrink-0">
                <button
                  type="button"
                  data-testid={`product-inquire-sealed-${idx}`}
                  onClick={() => prefillContact(item.name)}
                  disabled={item.qty === 0}
                  className="inline-flex items-center gap-2 bg-[#FF1F1F] text-white border-4 border-black px-4 py-2 font-heading uppercase text-sm tracking-wider shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] active:translate-x-0 active:translate-y-0 active:shadow-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <MessageSquare size={16} />
                  {item.qty === 0 ? "Sold Out" : "Ask About This"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Products() {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const current = CATEGORIES.find((c) => c.id === active);

  const totalSealedUnits = useMemo(
    () => SEALED_ITEMS.reduce((s, i) => s + (i.qty || 0), 0),
    []
  );

  return (
    <section
      id="products"
      data-testid="products-section"
      className="bg-[#F4F4F5] border-b-4 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="tag-chip tag-chip-red" data-testid="products-eyebrow">
              /// What We Stock
            </span>
            <h2
              data-testid="products-heading"
              className="mt-6 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              The <span className="text-[#FF1F1F]">Catalog</span>.
            </h2>
          </div>
          <p className="max-w-md text-base text-black/75 leading-relaxed">
            Sealed inventory is listed live below — search, filter, and ask
            about anything in the case. Slabs and singles rotate constantly and
            are kept in-shop only.
          </p>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CATEGORIES.map((cat) => {
            const isActive = cat.id === active;
            return (
              <button
                key={cat.id}
                data-testid={`category-card-${cat.id}`}
                onClick={() => setActive(cat.id)}
                className={`nb-card text-left flex flex-col h-full ${
                  isActive ? "ring-4 ring-[#FF1F1F] ring-offset-0" : ""
                }`}
              >
                <div className="aspect-[5/4] w-full overflow-hidden border-b-4 border-black relative">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 tag-chip tag-chip-red">
                    {cat.tag}
                  </div>
                  <div
                    className="absolute top-3 right-3 tag-chip"
                    data-testid={`category-stock-${cat.id}`}
                  >
                    {cat.mode === "inventory" ? `${totalSealedUnits} In Stock` : "In-Shop Only"}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading uppercase text-4xl leading-none">
                      {cat.title}
                    </h3>
                    <ArrowUpRight
                      size={28}
                      className={isActive ? "text-[#FF1F1F]" : "text-black"}
                      strokeWidth={2.5}
                    />
                  </div>
                  <p className="mt-3 text-sm text-black/75 leading-relaxed flex-1">
                    {cat.blurb}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Content panel for active category */}
        <div className="mt-10 nb-card bg-white" data-testid="products-list">
          <div className="px-5 md:px-7 py-4 bg-black text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b-4 border-black">
            <div className="flex items-center gap-3">
              <span className="tag-chip tag-chip-red">{current.tag}</span>
              <span className="font-heading uppercase text-2xl sm:text-3xl">
                {current.mode === "inventory"
                  ? `Current ${current.title} Inventory`
                  : `${current.title} — Visit The Shop`}
              </span>
            </div>
            <span className="font-mono text-xs uppercase tracking-widest text-white/80">
              {current.mode === "inventory"
                ? "Updated from Collectr"
                : "Inventory rotates · Book to view"}
            </span>
          </div>

          {current.mode === "inventory" ? (
            <SealedInventory />
          ) : (
            <div
              className="px-6 md:px-10 py-10 md:py-14 flex flex-col items-start gap-6"
              data-testid={`in-shop-panel-${current.id}`}
            >
              <p className="font-heading uppercase text-3xl sm:text-4xl leading-[1.05] max-w-2xl">
                {current.title} <span className="text-[#FF1F1F]">are kept in-shop only.</span>
              </p>
              <p className="text-base sm:text-lg text-black/75 leading-relaxed max-w-2xl">
                {current.inShopNote}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid={`in-shop-cta-book-${current.id}`}
                  onClick={() => scrollToId("contact")}
                  className="nb-btn-primary"
                >
                  <CalendarCheck2 size={20} />
                  Book A Visit
                </button>
                <button
                  type="button"
                  data-testid={`in-shop-cta-ask-${current.id}`}
                  onClick={() =>
                    prefillContact(
                      current.title === "Slabs"
                        ? "a graded slab (please share what you're hunting)"
                        : "a specific single (please share your wantlist)"
                    )
                  }
                  className="nb-btn-ghost"
                >
                  <MessageSquare size={18} />
                  Ask About {current.title}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
